import React, { useState, useMemo, useRef, useEffect } from 'react';

// Clean, minimal Tabs component with animated underline indicator (MUI-like)
const Tabs = ({ tabs = [], initialActiveId, activeId, onChange, className = '', children }) => {
  const defaultId = initialActiveId ?? (tabs[0] && tabs[0].id);
  const [internalActive, setInternalActive] = useState(defaultId);

  const current = activeId !== undefined ? activeId : internalActive;
  const tabIndexMap = useMemo(() => tabs.map((t) => t.id), [tabs]);

  const handleSelect = (id) => {
    if (onChange) onChange(id);
    if (activeId === undefined) setInternalActive(id);
  };

  const handleKeyDown = (e, idx) => {
    const last = tabIndexMap.length - 1;
    let nextIndex = idx;
    if (e.key === 'ArrowRight') nextIndex = idx === last ? 0 : idx + 1;
    else if (e.key === 'ArrowLeft') nextIndex = idx === 0 ? last : idx - 1;
    else return;

    e.preventDefault();
    const id = tabIndexMap[nextIndex];
    handleSelect(id);
    const btn = document.querySelector(`[data-tab-btn="${id}"]`);
    if (btn) btn.focus();
  };

  const panels = React.Children.toArray(children);

  const tabsRef = useRef([]);
  const containerRef = useRef(null);
  const indicatorRef = useRef(null);

  const updateIndicator = () => {
    const idx = tabIndexMap.indexOf(current);
    if (idx === -1) {
      if (indicatorRef.current) indicatorRef.current.style.width = '0px';
      return;
    }
    const btn = tabsRef.current[idx];
    const container = containerRef.current;
    const indicator = indicatorRef.current;
    if (!btn || !container || !indicator) return;

    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const left = btnRect.left - containerRect.left + container.scrollLeft;
    const width = btnRect.width;

    indicator.style.transform = `translateX(${left}px)`;
    indicator.style.width = `${width}px`;
  };

  useEffect(() => {
    updateIndicator();
    const onResize = () => updateIndicator();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, tabs]);

  return (
    <div className={`grid grid-rows-[auto_1fr] overflow-hidden ${className}`}>
      <div
        ref={containerRef}
        role='tablist'
        aria-orientation='horizontal'
        className='relative overflow-x-auto whitespace-nowrap'
      >
        <div className='border-b-2 border-gray-300'>
          <div className='inline-flex gap-2'>
            {tabs.map((t, idx) => {
              const isActive = t.id === current;
              return (
                <button
                  key={t.id}
                  ref={(el) => (tabsRef.current[idx] = el)}
                  data-tab-btn={t.id}
                  role='tab'
                  aria-selected={isActive}
                  aria-controls={`tab-panel-${t.id}`}
                  id={`tab-${t.id}`}
                  onClick={() => !t.disabled && handleSelect(t.id)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  disabled={t.disabled}
                  className={`px-4 py-2 pb-3 -mb-px text-base font-medium focus:outline-none transition-colors ${
                    isActive ? 'text-blue-600' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {t.title}
                </button>
              );
            })}
          </div>
        </div>

        <span
          ref={indicatorRef}
          className='absolute bottom-0 left-0 h-0.5 bg-blue-600 transition-all duration-300'
          style={{ width: 0, transform: 'translateX(0px)' }}
        />
      </div>

      <div className='mt-4 overflow-hidden'>
        {panels.map((p) => {
          const tabId = p.props && p.props.tabId;
          if (tabId === undefined) return null;
          const isActive = tabId === current;
          return (
            <div
              key={tabId}
              id={`tab-panel-${tabId}`}
              role='tabpanel'
              aria-labelledby={`tab-${tabId}`}
              hidden={!isActive}
              className='h-full'
            >
              {isActive ? p : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Tabs;
